import { Injectable, inject } from '@angular/core';
import { Action, createSelector, Selector, State, type StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { firstValueFrom } from 'rxjs';
import type { TestStateModel } from '../../../services/api/models/TestStateModel';
import { TelemetryApiService } from '../../../services/api/telemetry.service';
import { GenerateEChartConfig } from '../multi-view/actions/other.actions';
import { MultiViewState } from '../multi-view/multi-view.state';
import {
  ApproveTestPhase,
  CreateTestPhase,
  DeleteTestPhase,
  HideAllPhases,
  LoadTestPhases,
  ResetAllTestPhasesToOriginal,
  ResetTestPhaseState,
  SetTestPhaseError,
  SetTestPhaseLoading,
  SetTestPhases,
  ShowAllPhases,
  TogglePhaseVisibility,
  UpdateTestPhase,
} from './test-phase.actions';
import { TEST_PHASE_STATE_DEFAULTS, type TestPhaseItem, type TestPhaseStateModel } from './test-phase.state.model';

@State<TestPhaseStateModel>({
  name: 'test_phase_state',
  defaults: TEST_PHASE_STATE_DEFAULTS,
})
@Injectable()
export class TestPhaseState {
  private readonly store = inject(Store);
  private readonly telemetryApi = inject(TelemetryApiService);

  // ─────────────────────────────────────────────────────────────────────────────
  // Selectors
  // ─────────────────────────────────────────────────────────────────────────────

  @Selector()
  static getPhases(state: TestPhaseStateModel): TestPhaseItem[] {
    return state?.phases ?? [];
  }

  @Selector()
  static getVisiblePhases(state: TestPhaseStateModel): TestPhaseItem[] {
    return state?.phases?.filter((p) => p.visible) ?? [];
  }

  @Selector()
  static getHiddenPhaseIds(state: TestPhaseStateModel): string[] {
    return state?.hiddenPhaseIds ?? [];
  }

  @Selector()
  static isLoading(state: TestPhaseStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static getError(state: TestPhaseStateModel): string | null {
    return state.error;
  }

  @Selector()
  static hasPhases(state: TestPhaseStateModel): boolean {
    return state.phases.length > 0;
  }

  /**
   * Get phases with visibility applied from hiddenPhaseIds
   */
  static readonly getPhasesWithVisibility = createSelector([TestPhaseState], (state: TestPhaseStateModel): TestPhaseItem[] => {
    const hiddenSet = new Set(state.hiddenPhaseIds);
    return state.phases.map((phase) => ({
      ...phase,
      visible: !hiddenSet.has(phase.identifier),
    }));
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────────

  @Action(LoadTestPhases)
  async loadTestPhases(ctx: StateContext<TestPhaseStateModel>): Promise<void> {
    // Only show the spinner on the very first load (no phases yet)
    const isInitialLoad = ctx.getState().phases.length === 0;
    if (isInitialLoad) {
      ctx.dispatch(new SetTestPhaseLoading(true));
    }
    ctx.dispatch(new SetTestPhaseError(null));

    try {
      const controls = this.store.selectSnapshot(MultiViewState.getSensorTableControlsOfCurrentView);

      if (!controls || controls.length === 0) {
        ctx.dispatch(new SetTestPhases([]));
        return;
      }

      // Derive unique group IDs from controls' parent identifiers
      const groupIds = [...new Set(controls.map((c) => c.entity?.parentIdentifier as string | undefined).filter((id): id is string => Boolean(id)))];

      if (groupIds.length === 0) {
        ctx.dispatch(new SetTestPhases([]));
        return;
      }

      const phases = await this.fetchTestPhasesFromApi(groupIds);
      ctx.dispatch(new SetTestPhases(phases));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load test phases';
      ctx.dispatch(new SetTestPhaseError(errorMessage));
      console.error('Error loading test phases:', error);
    } finally {
      ctx.dispatch(new SetTestPhaseLoading(false));
    }
  }

  @Action(SetTestPhases)
  setTestPhases(ctx: StateContext<TestPhaseStateModel>, { phases }: SetTestPhases): void {
    const hiddenSet = new Set(ctx.getState().hiddenPhaseIds);
    const phasesWithVisibility = phases.map((phase) => ({
      ...phase,
      visible: !hiddenSet.has(phase.identifier),
    }));
    ctx.setState(patch({ phases: phasesWithVisibility }));
  }

  @Action(TogglePhaseVisibility)
  togglePhaseVisibility(ctx: StateContext<TestPhaseStateModel>, { phaseId }: TogglePhaseVisibility): void {
    const state = ctx.getState();
    const hiddenSet = new Set(state.hiddenPhaseIds);

    if (hiddenSet.has(phaseId)) {
      hiddenSet.delete(phaseId);
    } else {
      hiddenSet.add(phaseId);
    }

    const hiddenPhaseIds = Array.from(hiddenSet);
    const phases = state.phases.map((phase) => ({
      ...phase,
      visible: !hiddenSet.has(phase.identifier),
    }));

    ctx.setState(patch({ hiddenPhaseIds, phases }));
    ctx.dispatch(new GenerateEChartConfig());
  }

  @Action(SetTestPhaseLoading)
  setLoading(ctx: StateContext<TestPhaseStateModel>, { isLoading }: SetTestPhaseLoading): void {
    ctx.setState(patch({ isLoading }));
  }

  @Action(SetTestPhaseError)
  setError(ctx: StateContext<TestPhaseStateModel>, { error }: SetTestPhaseError): void {
    ctx.setState(patch({ error }));
  }

  @Action(ResetTestPhaseState)
  resetState(ctx: StateContext<TestPhaseStateModel>): void {
    ctx.setState(TEST_PHASE_STATE_DEFAULTS);
  }

  @Action(ShowAllPhases)
  showAllPhases(ctx: StateContext<TestPhaseStateModel>): void {
    const phases = ctx.getState().phases.map((phase) => ({
      ...phase,
      visible: true,
    }));
    ctx.setState(patch({ hiddenPhaseIds: [], phases }));
    ctx.dispatch(new GenerateEChartConfig());
  }

  @Action(HideAllPhases)
  hideAllPhases(ctx: StateContext<TestPhaseStateModel>): void {
    const phases = ctx.getState().phases;
    const hiddenPhaseIds = phases.map((p) => p.identifier);
    const hiddenPhases = phases.map((phase) => ({ ...phase, visible: false }));
    ctx.setState(patch({ hiddenPhaseIds, phases: hiddenPhases }));
    ctx.dispatch(new GenerateEChartConfig());
  }

  @Action(CreateTestPhase)
  async createTestPhase(ctx: StateContext<TestPhaseStateModel>, { groupId, body }: CreateTestPhase): Promise<void> {
    try {
      await firstValueFrom(this.telemetryApi.createTestState(groupId, body));
      // Silently reload phases and regenerate chart config
      await firstValueFrom(ctx.dispatch(new LoadTestPhases()));
      ctx.dispatch(new GenerateEChartConfig());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create test phase';
      ctx.dispatch(new SetTestPhaseError(errorMessage));
      console.error('Error creating test phase:', error);
    }
  }

  @Action(UpdateTestPhase)
  async updateTestPhase(ctx: StateContext<TestPhaseStateModel>, { testStateId, body }: UpdateTestPhase): Promise<void> {
    try {
      await firstValueFrom(this.telemetryApi.updateTestState(testStateId, body));
      // Silently reload phases and regenerate chart config
      await firstValueFrom(ctx.dispatch(new LoadTestPhases()));
      ctx.dispatch(new GenerateEChartConfig());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update test phase';
      ctx.dispatch(new SetTestPhaseError(errorMessage));
      console.error('Error updating test phase:', error);
    }
  }

  @Action(DeleteTestPhase)
  async deleteTestPhase(ctx: StateContext<TestPhaseStateModel>, { testStateId }: DeleteTestPhase): Promise<void> {
    try {
      await firstValueFrom(this.telemetryApi.deleteTestState(testStateId));
      // Silently reload phases and regenerate chart config
      await firstValueFrom(ctx.dispatch(new LoadTestPhases()));
      ctx.dispatch(new GenerateEChartConfig());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete test phase';
      ctx.dispatch(new SetTestPhaseError(errorMessage));
      console.error('Error deleting test phase:', error);
    }
  }

  @Action(ApproveTestPhase)
  async approveTestPhase(ctx: StateContext<TestPhaseStateModel>, { testStateId }: ApproveTestPhase): Promise<void> {
    try {
      await firstValueFrom(this.telemetryApi.approveTestState(testStateId));
      // Silently reload phases and regenerate chart config
      await firstValueFrom(ctx.dispatch(new LoadTestPhases()));
      ctx.dispatch(new GenerateEChartConfig());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve test phase';
      ctx.dispatch(new SetTestPhaseError(errorMessage));
      console.error('Error approving test phase:', error);
    }
  }

  @Action(ResetAllTestPhasesToOriginal)
  async resetAllTestPhasesToOriginal(ctx: StateContext<TestPhaseStateModel>): Promise<void> {
    const phases = ctx.getState().phases.filter((p) => !p.isApproved);
    const phasesToReset = phases.filter((p) => p.phaseName !== p.origName || p.startDate !== p.startOrigDate || p.endDate !== p.endOrigDate);

    if (phasesToReset.length === 0) {
      return;
    }

    try {
      await Promise.all(
        phasesToReset.map((p) =>
          firstValueFrom(
            this.telemetryApi.updateTestState(p.identifier, {
              name: p.origName,
              startDate: p.startOrigDate,
              endDate: p.endOrigDate,
              color: p.color,
            })
          )
        )
      );
      await firstValueFrom(ctx.dispatch(new LoadTestPhases()));
      ctx.dispatch(new GenerateEChartConfig());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset test phases to original';
      ctx.dispatch(new SetTestPhaseError(errorMessage));
      console.error('Error resetting test phases to original:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Fetches test phases from the API for multiple group IDs and merges the results.
   */
  private async fetchTestPhasesFromApi(groupIds: string[]): Promise<TestPhaseItem[]> {
    const allApiPhases: TestStateModel[] = [];

    for (const groupId of groupIds) {
      const phases = await firstValueFrom(this.telemetryApi.getTestStates(groupId));
      allApiPhases.push(...phases);
    }

    return this.convertApiPhasesToItems(allApiPhases);
  }

  /**
   * Converts API response models to TestPhaseItem array.
   * Deduplicates by identifier in case the same phase appears across groups.
   */
  private convertApiPhasesToItems(apiPhases: TestStateModel[]): TestPhaseItem[] {
    const seen = new Set<string>();
    const result: TestPhaseItem[] = [];

    for (const apiPhase of apiPhases) {
      if (seen.has(apiPhase.identifier)) {
        continue;
      }
      seen.add(apiPhase.identifier);

      result.push({
        identifier: apiPhase.identifier,
        phaseName: apiPhase.name,
        origName: apiPhase.origName,
        configurationId: apiPhase.configurationId,
        startDate: apiPhase.startDate,
        endDate: apiPhase.endDate,
        startOrigDate: apiPhase.startOrigDate,
        endOrigDate: apiPhase.endOrigDate,
        color: apiPhase.color,
        visible: true,
        approvedBy: apiPhase.approvedBy,
        isApproved: apiPhase.isApproved,
        createdBy: apiPhase.createdBy,
        createdAt: apiPhase.createdAt,
        updatedAt: apiPhase.updatedAt,
        updatedBy: apiPhase.updatedBy,
      });
    }

    return result;
  }
}
